import React, {
    useCallback,
    useMemo,
    useState,
} from 'react';
import useSimpleApiHandler from '@frontend/jetlend-core/src/hooks/useSimpleApiHandler';
import TabManager from '@frontend/jetlend-web-ui/src/ui/navs/Tabs/TabManager';
import TabContainer from '@frontend/jetlend-web-ui/src/ui/navs/Tabs/TabContainer';
import {
    isMobile,
    isNotDesktop,
    useWindowSize,
} from '@frontend/jetlend-web-ui/src/utils/responsive';
import Button from '@frontend/jetlend-web-ui/src/ui/inputs/Button/Button';
import { buildClassNames } from '@frontend/jetlend-web-ui/src/utils/classNameUtils';
import Link from 'next/link';
import OffscreenLoader from '@app/ui/OffscreenLoader/OffscreenLoader';
import CoursesItemCard from './CoursesItemCard/CoursesItemCard';
import { getUserCoursesHandler } from '@app/ducks/academy/course';
import {
    CourseComplexity,
    CourseComplexityDictionary,
    IAchievementItemApiModel,
    ICourseInformationApiModel,
} from '@app/models/features/course';
import CoursesSlider from './CoursesSlider/CoursesSlider';
import {
    getAchievementsById,
    getCoursesById,
} from '../academy.helper';

import styles from './CoursesSection.module.scss';

export interface IProps {
    /**
     * Список ачивок
     */
    achievements: IAchievementItemApiModel[];
    /**
     * Список всех курсов
     */
    courses: ICourseInformationApiModel[];
    /**
     * Карточки с белым фоном
     */
    whiteCards?: boolean;
}

/**
 * Функция, для получения курсов по выбранной сложности
 * @param courses список курсов
 * @param complexity выбранная сложность
 * @returns отсортированный по сложности список курсов
 */
const getCourses = (courses: ICourseInformationApiModel[], complexity: CourseComplexity) => courses?.filter(item => item.complexity === complexity);

/**
 * Компонент для отрисовки табов с курсами
 */
const CoursesSection:React.FC<IProps> = props => {
    const {
        achievements,
        courses,
    } = props;
    const [activeTabId, setActiveTabId] = useState(CourseComplexity.Newbie);
    const [userCourses] = useSimpleApiHandler(getUserCoursesHandler);
    const { width: screenWidth } = useWindowSize();
    const isNotDesktopScreen = isNotDesktop(screenWidth);
    const isMobileScreen = isMobile(screenWidth);

    const newbieCourses = useMemo(() => getCourses(courses, CourseComplexity.Newbie), [courses]);
    const experiencedCourses = useMemo(() => getCourses(courses, CourseComplexity.Experienced), [courses]);
    const expertCourses = useMemo(() => getCourses(courses, CourseComplexity.Expert), [courses]);
    const achievementsMap = useMemo(() => getAchievementsById(achievements), [achievements]);
    const coursesMap = useMemo(() => getCoursesById(userCourses), [userCourses]);

    const coursesArray = {
        [CourseComplexity.Newbie]: newbieCourses,
        [CourseComplexity.Experienced]: experiencedCourses,
        [CourseComplexity.Expert]: expertCourses,
    };

    const getTabClassnames = (tabId: CourseComplexity) => buildClassNames(styles, [
        'tab',
        activeTabId === tabId && 'tab--active',
    ]);

    const didActiveTabChanged = useCallback((tabId: CourseComplexity) => setActiveTabId(tabId), []);

    if (!courses || courses?.length === 0) {
        return null;
    }

    return (
        <OffscreenLoader>
            {({
                ref,
                inView,
            }) => (
                <div ref={ref} className="container">
                    {inView && (
                        <TabManager
                            withBorder
                            size={isNotDesktopScreen ? 'small' : 'large'}
                            key="courses"
                            activeTabId={activeTabId}
                            onChange={didActiveTabChanged}
                            disableScroll={!isMobileScreen}
                            className={styles['tabs']}
                            actions={
                                !isMobileScreen && (
                                    <Link href="/academy/courses" className={styles['action']}>
                                        Все курсы ({courses.length})
                                    </Link>
                                )}
                        >
                            {Object.keys(CourseComplexityDictionary).map(item => (
                                <TabContainer
                                    key={item}
                                    id={item}
                                    title={(
                                        <div className={getTabClassnames(item as CourseComplexity)}>
                                            {CourseComplexityDictionary[item]}
                                            <div className={styles['badge']}>({coursesArray[item].length})</div>
                                        </div>
                                    )}
                                >
                                    <CoursesSlider>
                                        {coursesArray[item]?.map((course: ICourseInformationApiModel) => (
                                            <CoursesItemCard
                                                key={course?.id}
                                                description={course?.description}
                                                url={`/academy/courses/${course?.slug}`}
                                                image={course?.preview_url}
                                                imageAlt={course?.preview_alt}
                                                complexity={course?.complexity}
                                                reward={achievementsMap?.[course?.achievement_id]}
                                                passed={false}
                                                blocksCount={course?.blocks_count}
                                                passedBlocksCount={coursesMap?.[course?.id]?.passed_blocks_count}
                                                estimateTime={coursesMap?.[course?.id]?.passed_estimated_time}
                                                white={props.whiteCards}
                                            />
                                        ))}
                                    </CoursesSlider>
                                </TabContainer>
                            ))}
                        </TabManager>
                    )}
                    {isMobileScreen && (
                        <div className={styles['button']}>
                            <Button
                                block
                                buttonType="button"
                                outline
                                text={`Все курсы (${courses.length})`}
                                href="/academy/courses"
                                external
                                noSpacing
                            />
                        </div>
                    )}
                </div>
            )}
        </OffscreenLoader>
    );
};

export default CoursesSection;